import { Typography, Box, Link } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Trans } from 'react-i18next'

import { RetrofitRecommendation as RetrofitRecommendationModel, RetrofitConfiguration, InfinityOption } from '../../models'
import { getBatteryModulesCount, getCapacity, getPower, getBatteryModulesDiff, getCapacityDiff, getPowerDiff, getCabinetSizes } from '../../utils'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useModals from 'src/Hooks/useModals'

const useStyles = makeStyles()(theme => ({
  rowElement: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  unit: {
    color: theme.palette.text.primary,
  },
  diffValue: {
    color: theme.palette.primary.dark,
  },
  valueContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  sizesContainer: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    backgroundColor: theme.palette.grey.grey07,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey.grey06}`,
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  linkContainer: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  accordionItem: {
    borderColor: theme.palette.grey.grey06,
  },
  accordionItemContent: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  },
}))

interface RetrofitRecommendationProps {
  title: string
  currentSystemValue: RetrofitConfiguration
  recommendationValue: RetrofitRecommendationModel | InfinityOption | undefined
  defaultOpened: boolean
  isInfinity?: boolean
}

/**
 * Component that displays a single retrofit recommendation for the current system
 */
const RetrofitRecommendation: React.FC<RetrofitRecommendationProps> = ({
  title,
  defaultOpened,
  currentSystemValue,
  recommendationValue,
  isInfinity = false,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { openInformationModal } = useModals()

  if (!recommendationValue) {
    return null
  }

  const numberExternalCabinets = (recommendationValue as RetrofitRecommendationModel)?.numberExternalCabinets ?? 0
  const info = (recommendationValue as RetrofitRecommendationModel)?.info
  const cabinetDemension = (recommendationValue as RetrofitRecommendationModel)?.cabinetDimension
  const formattedCabinetSizes = getCabinetSizes(cabinetDemension)

  const getDiffTranslationKey = (key: string): string => `retrofit-check.available.suggestions${isInfinity ? '.infinity' : ''}.diff.${key}`

  const rowElements = [
    {
      label: t('retrofit-check.available.suggestions.battery-modules-title'),
      value: (
        <Trans
          i18nKey={'retrofit-check.available.suggestions.total.betteries-count'}
          values={{ value: getBatteryModulesCount(recommendationValue) }}
          components={{ value: <Typography variant="h2" />, subtitle: <Typography variant="subtitle2" /> }}
        />
      ),
      diffValue: (
        <Trans
          i18nKey={getDiffTranslationKey('betteries-count')}
          values={{ value: getBatteryModulesDiff(currentSystemValue, recommendationValue) }}
          components={{ value: <Typography variant="h2" className={classes.diffValue} />, subtitle: <Typography variant="subtitle2" /> }}
        />
      ),
    },
    {
      label: t('retrofit-check.available.suggestions.power-title'),
      value: (
        <Trans
          i18nKey={'retrofit-check.available.suggestions.total.power'}
          values={{ value: getPower(recommendationValue) }}
          components={{
            value: <Typography variant="h2" />,
            unit: <Typography component={'span'} variant="subtitle2" className={classes.unit} />,
            subtitle: <Typography variant="subtitle2" />,
          }}
        />
      ),
      diffValue: (
        <Trans
          i18nKey={getDiffTranslationKey('power')}
          values={{ value: getPowerDiff(currentSystemValue, recommendationValue) }}
          components={{
            value: <Typography variant="h2" className={classes.diffValue} />,
            unit: <Typography component={'span'} variant="subtitle2" className={classes.unit} />,
            subtitle: <Typography variant="subtitle2" />,
          }}
        />
      ),
    },
    {
      label: t('retrofit-check.available.suggestions.capacity-title'),
      value: (
        <Trans
          i18nKey={'retrofit-check.available.suggestions.total.capacity'}
          values={{ value: getCapacity(recommendationValue) }}
          components={{
            value: <Typography variant="h2" />,
            unit: <Typography component={'span'} variant="subtitle2" className={classes.unit} />,
            subtitle: <Typography variant="subtitle2" />,
          }}
        />
      ),
      diffValue: (
        <Trans
          i18nKey={getDiffTranslationKey('capacity')}
          values={{ value: getCapacityDiff(currentSystemValue, recommendationValue) }}
          components={{
            value: <Typography variant="h2" className={classes.diffValue} />,
            unit: <Typography component={'span'} variant="subtitle2" className={classes.unit} />,
            subtitle: <Typography variant="subtitle2" />,
          }}
        />
      ),
    },
  ]

  return (
    <AccordionItem
      title={title}
      expanded={defaultOpened}
      elevation={0}
      className={classes.accordionItem}
      contentClassName={classes.accordionItemContent}
    >
      {isInfinity && (
        <Typography variant="subtitle2" className={classes.linkContainer}>
          <Trans
            i18nKey={'retrofit-check.available.suggestions.infinity.text'}
            components={{
              page: (
                <Link
                  component={'button'}
                  onClick={() =>
                    openInformationModal({
                      title: t('retrofit-check.available.suggestions.infinity.details.title'),
                      textKey: 'retrofit-check.available.suggestions.infinity.details.text',
                    })
                  }
                />
              ),
            }}
          />
        </Typography>
      )}
      {info && (
        <Typography variant="subtitle2" className={classes.linkContainer}>
          <Trans
            i18nKey={`retrofit-check.available.suggestions.mit-${info}-link-text`}
            components={{
              page: (
                <Link
                  component={'button'}
                  onClick={() =>
                    openInformationModal({
                      title: t(`retrofit-check.available.suggestions.${info}.details.title`),
                      textKey: `retrofit-check.available.suggestions.${info}.details.text`,
                    })
                  }
                />
              ),
            }}
          />
        </Typography>
      )}
      <Box>
        {rowElements.map(({ label, value, diffValue }) => (
          <Box className={classes.rowElement} key={label}>
            <Typography variant="h5">{label}</Typography>
            <Box className={classes.valueContainer}>{diffValue}</Box>
            <Box className={classes.valueContainer}>{value}</Box>
          </Box>
        ))}
      </Box>
      {+numberExternalCabinets > 0 && (
        <Box className={classes.sizesContainer}>
          <Trans
            i18nKey={
              numberExternalCabinets > 1
                ? 'retrofit-check.available.suggestions.battery-cabinet-info-multiple'
                : 'retrofit-check.available.suggestions.battery-cabinet-info-single'
            }
            values={{
              cabinetsCount: numberExternalCabinets,
              depth: formattedCabinetSizes?.depth ?? 0,
              width: formattedCabinetSizes?.width ?? 0,
              height: formattedCabinetSizes?.height ?? 0,
            }}
            components={{
              row: <Typography variant="body2" />,
              bold: <Typography variant="h5" component={'span'} />,
            }}
          />
        </Box>
      )}
    </AccordionItem>
  )
}

export default RetrofitRecommendation
